import { InviteCreateDto, InviteCreateDtoRoleEnum, UserNestedSelectFromCompanyDto } from '@@types/api.types.ts';
import { errorToast } from '@hooks/toast.hook.tsx';
import { useSubmitInvites } from '@queries/invite.query.ts';
import { useCallback, useState } from 'react';

type UseCompanyProfileModalInvitesResult = {
  handleInviteAdd: (invite: InviteCreateDto) => Promise<void>;
  handleInviteChangeRole: (invite: InviteCreateDto, role: InviteCreateDtoRoleEnum) => Promise<void>;
  handleInviteRemove: (invite: InviteCreateDto) => Promise<void>;
  handleUserChangeRole: (_user: UserNestedSelectFromCompanyDto, _role: string) => Promise<void>;
  handleUserRemove: (_user: UserNestedSelectFromCompanyDto) => Promise<void>;
  invites: InviteCreateDto[];
  isInvitesPending: boolean;
  resetInvites: () => void;
  submitCompanyInvites: (companyId: string) => Promise<boolean>;
};

export const useCompanyProfileModalInvites = (): UseCompanyProfileModalInvitesResult => {
  const [invites, setInvites] = useState<InviteCreateDto[]>([]);
  const { mutateAsync: submitInvites, isPending: isInvitesPending } = useSubmitInvites();

  const handleInviteAdd = useCallback(async (invite: InviteCreateDto): Promise<void> => {
    setInvites((previousInvites) => [...previousInvites, invite]);
  }, []);

  const handleInviteChangeRole = useCallback(
    async (invite: InviteCreateDto, role: InviteCreateDtoRoleEnum): Promise<void> => {
      setInvites((previousInvites) =>
        previousInvites.map((previousInvite) => {
          if (previousInvite.email === invite.email) {
            return { ...previousInvite, role };
          }

          return previousInvite;
        }),
      );
    },
    [],
  );

  const handleInviteRemove = useCallback(async (invite: InviteCreateDto): Promise<void> => {
    setInvites((previousInvites) => previousInvites.filter((previousInvite) => previousInvite.email !== invite.email));
  }, []);

  const handleUserChangeRole = useCallback(
    async (_user: UserNestedSelectFromCompanyDto, _role: string): Promise<void> => {},
    [],
  );

  const handleUserRemove = useCallback(async (_user: UserNestedSelectFromCompanyDto): Promise<void> => {}, []);

  const resetInvites = useCallback(() => {
    setInvites([]);
  }, []);

  const submitCompanyInvites = useCallback(
    async (companyId: string): Promise<boolean> => {
      if (invites.length === 0) {
        return true;
      }

      const invitesWithCompanyId = invites.map((invite) => ({
        ...invite,
        companyId,
      }));

      try {
        await submitInvites(invitesWithCompanyId);
        return true;
      } catch {
        errorToast({ description: 'Failed to send invites.' });
      }

      return false;
    },
    [invites, submitInvites],
  );

  return {
    handleInviteAdd,
    handleInviteChangeRole,
    handleInviteRemove,
    handleUserChangeRole,
    handleUserRemove,
    invites,
    isInvitesPending,
    resetInvites,
    submitCompanyInvites,
  };
};
